import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { humaniseViolation } from "../../api/rules.api.js";
import { Card, GhostButton, DangerButton, Icon, colors, } from "../ui/index.js";
import { useConfirm } from "../ui/ConfirmProvider.js";
import { VenueChip } from "../ui/VenueChip.js";
import { addMinutes } from "../../lib/format-session.js";
const DAY_LABEL = [
    "Sundays",
    "Mondays",
    "Tuesdays",
    "Wednesdays",
    "Thursdays",
    "Fridays",
    "Saturdays",
];
function scheduleSummary(rule) {
    const day = DAY_LABEL[rule.dayOfWeek] ?? "";
    const end = addMinutes(rule.startTime, rule.durationMin);
    return `${day} ${rule.startTime}–${end}`;
}
const buttonRowStyle = {
    display: "flex",
    gap: 6,
    alignItems: "center",
    flexWrap: "wrap",
};
function stop(e) {
    e.stopPropagation();
}
export function RuleRow({ rule, onEdit, onToggleActive, onDelete }) {
    const { confirm } = useConfirm();
    const [showWhy, setShowWhy] = useState(false);
    const navigate = useNavigate();
    const blocked = Boolean(rule.eligibility && !rule.eligibility.ok);
    const status = !rule.active ? "disabled" : blocked ? "blocked" : "active";
    const STATUS = {
        active: { color: colors.success, label: "Active" },
        disabled: { color: colors.textTertiary, label: "Paused" },
        blocked: { color: colors.danger, label: "Blocked" },
    }[status];
    const handleDelete = async () => {
        const ok = await confirm("Delete booking preference?", `Deleting "${rule.name}" will also cancel any planned future sessions generated from this preference. Already-booked courts will be cancelled with the venue.`, { danger: true, confirmText: "Delete preference" });
        if (ok) {
            onDelete(rule);
        }
    };
    const violations = rule.eligibility?.violations ?? [];
    const goToDetail = () => {
        navigate(`/rules/${rule.id}`);
    };
    const onCardKey = (e) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            goToDetail();
        }
    };
    return (_jsx(Card, { style: { padding: 0, overflow: "hidden", opacity: status === "disabled" ? 0.78 : 1 }, children: _jsxs("div", { role: "link", tabIndex: 0, "aria-label": `Open ${scheduleSummary(rule)} preference`, onClick: goToDetail, onKeyDown: onCardKey, style: { display: "flex", cursor: "pointer" }, onMouseEnter: (e) => {
                e.currentTarget.style.background = colors.surfaceSecondary;
            }, onMouseLeave: (e) => {
                e.currentTarget.style.background = "transparent";
            }, children: [_jsx("div", { "aria-hidden": true, style: {
                        width: 4,
                        background: STATUS.color,
                        flexShrink: 0,
                    } }), _jsx("div", { style: { flex: 1, padding: 14 }, children: _jsxs("div", { style: {
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 16,
                            alignItems: "flex-start",
                            justifyContent: "space-between",
                        }, children: [_jsxs("div", { style: { flex: 1, minWidth: 220 }, children: [_jsxs("div", { style: {
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 8,
                                            flexWrap: "wrap",
                                            marginBottom: 4,
                                        }, children: [_jsx("span", { "aria-hidden": true, style: {
                                                    width: 8,
                                                    height: 8,
                                                    borderRadius: 999,
                                                    background: STATUS.color,
                                                } }), _jsx("span", { style: {
                                                    fontSize: "0.7rem",
                                                    fontWeight: 700,
                                                    color: STATUS.color,
                                                    letterSpacing: "0.06em",
                                                    textTransform: "uppercase",
                                                }, children: STATUS.label }), blocked && (_jsx("button", { type: "button", onClick: (e) => {
                                                    stop(e);
                                                    setShowWhy((v) => !v);
                                                }, "aria-expanded": showWhy, style: {
                                                    background: "transparent",
                                                    border: "none",
                                                    color: colors.danger,
                                                    fontSize: "0.72rem",
                                                    cursor: "pointer",
                                                    padding: 0,
                                                    textDecoration: "underline",
                                                }, children: showWhy ? "Hide" : "Why?" }))] }), _jsx("div", { style: {
                                            fontSize: "1rem",
                                            fontWeight: 700,
                                            color: colors.text,
                                            letterSpacing: "-0.01em",
                                        }, children: scheduleSummary(rule) }), _jsxs("div", { style: {
                                            display: "flex",
                                            flexWrap: "wrap",
                                            gap: 12,
                                            marginTop: 6,
                                            fontSize: "0.8rem",
                                            color: colors.textSecondary,
                                        }, children: [_jsx(VenueChip, { venueId: rule.venueId }), _jsxs("span", { children: [_jsx(Icon, { name: "users", size: 12 }), " ", rule.courtCount, " court", rule.courtCount === 1 ? "" : "s"] })] }), blocked && showWhy && (_jsxs("div", { style: {
                                            marginTop: 10,
                                            padding: 10,
                                            borderRadius: 6,
                                            background: colors.dangerLight,
                                            color: colors.danger,
                                            fontSize: "0.78rem",
                                        }, children: [_jsx("strong", { children: "Why it's blocked:" }), _jsx("ul", { style: { margin: "4px 0 0 0", paddingLeft: 18 }, children: violations.length === 0 ? (_jsx("li", { children: "Doesn't fit your current permitted booking windows. Adjust the day/time/courts." })) : (violations.map((v, i) => (_jsx("li", { children: humaniseViolation(v) }, `${v.type ?? v.code ?? "?"}-${i}`)))) })] }))] }), _jsxs("div", { style: buttonRowStyle, onClick: stop, onKeyDown: stop, children: [_jsxs(GhostButton, { onClick: () => onEdit(rule), children: [_jsx(Icon, { name: "pencil", size: 12 }), " Edit"] }), _jsx(GhostButton, { onClick: () => onToggleActive(rule), children: rule.active ? (_jsxs(_Fragment, { children: [_jsx(Icon, { name: "pause", size: 12 }), " Pause"] })) : (_jsxs(_Fragment, { children: [_jsx(Icon, { name: "play", size: 12 }), " Resume"] })) }), _jsxs(DangerButton, { onClick: () => void handleDelete(), children: [_jsx(Icon, { name: "trash", size: 12 }), " Delete"] })] })] }) })] }) }));
}
